/**
 * Der vollstaendige Belegbestand des Anlagedialogs — Ladesteuerung.
 *
 * Die Belegliste laedt ihre Belege OHNE Positionen. Der Anlagedialog braucht
 * sie aber: computeOpenPositions und openByMonth lesen aus den Zeilen der
 * vorhandenen Belege, welche Protokolleintraege bereits abgerechnet sind. Also
 * wird der volle Bestand erst beim Oeffnen des Dialogs geholt.
 *
 * Diese Steuerung steht bewusst NEBEN der Ansicht und nicht in einem Effekt:
 *
 * 1. Ein Effekt, der seinen eigenen Ladezustand im Dep-Array fuehrt und ihn im
 *    eigenen Rumpf setzt, wertet sich sofort neu aus, raeumt den vorherigen
 *    Durchlauf ab und verwirft damit sein eigenes Ergebnis. Der Dialog bliebe
 *    fuer immer bei "Offene Mengen werden ermittelt ...". Ausgeloest wird
 *    deshalb dort, wo der Bedarf entsteht: beim Oeffnen und beim Wiederholen.
 * 2. Ohne React ist die Logik pruefbar — die Faelle (oeffnen, waehrend des
 *    Ladens schliessen, sofort wieder oeffnen, Verwerfen bei offenem Dialog)
 *    sind genau die, in denen der Zustand dauerhaft haengenbleiben kann, und im
 *    Projekt gibt es keine DOM-Testumgebung, mit der sich das an der Ansicht
 *    selbst zeigen liesse.
 *
 * Welche Antwort eingespielt werden darf, entscheidet allein die laufende
 * Nummer der Anforderung: jedes Oeffnen, Schliessen, Wiederholen und jedes
 * Verwerfen zaehlt sie hoch, und eine ueberholte Antwort wird verworfen. Sonst
 * entschiede bei zwei schnellen Oeffnen/Schliessen-Zyklen die Reihenfolge der
 * Antworten darueber, welcher Bestand gilt — der Dialog rechnete dann mit dem
 * von vorhin, und zwar ohne jedes Anzeichen.
 */

/** 'failed' zeigt im Dialog den Wiederholen-Knopf statt der Ladezeile. */
export type PickerStockLoad = 'idle' | 'loading' | 'failed';

export const PICKER_STOCK_ERROR =
    'Die bereits abgerechneten Mengen konnten nicht geladen werden. Ohne sie lässt sich nicht sagen, was noch offen ist.';

export interface PickerStockPorts<T> {
    /** Holt den vollstaendigen Bestand. */
    fetchAll: () => Promise<T[]>;
    /** Uebernimmt den Bestand in die Ansicht; null heisst "nicht vorhanden". */
    setStock: (stock: T[] | null) => void;
    setLoad: (load: PickerStockLoad) => void;
    /** Fehler an die Ansicht — sie zeigt ihn im Dialog. */
    reportError: (message: string) => void;
}

export interface PickerStock {
    /** Dialog geoeffnet: holt den Bestand, falls keiner vorliegt. */
    open: () => void;
    /** Dialog geschlossen: ein laufendes Laden gilt als verworfen. */
    close: () => void;
    /** Ausdruecklicher neuer Versuch nach einem Fehlschlag. */
    retry: () => void;
    /**
     * Der Bestand ist ueberholt (die Liste wurde neu geladen). Steht der Dialog
     * dabei offen, wird sofort neu geholt.
     */
    invalidate: () => void;
}

const messageOf = (error: unknown): string =>
    (error instanceof Error && error.message) || PICKER_STOCK_ERROR;

export const createPickerStock = <T>(ports: PickerStockPorts<T>): PickerStock => {
    /** Laufende Nummer der Anforderung; nur die juengste darf einspielen. */
    let request = 0;
    /** Liegt ein Bestand vor? Bestimmt, ob das Oeffnen ueberhaupt holen muss. */
    let held = false;
    /** Laeuft gerade eine Abfrage? Verhindert eine zweite daneben. */
    let running = false;
    /**
     * Steht der Dialog offen?
     *
     * Nur er entscheidet, ob ein verworfener Bestand SOFORT wieder geholt
     * werden muss: ein offener Dialog ohne Bestand zeigt seine Ladezeile, und
     * ohne neuen Versuch liefe sie ohne Fortschritt und ohne Wiederholmoeglich-
     * keit weiter. Bei geschlossenem Dialog waere das Holen dagegen Arbeit auf
     * Verdacht — dort genuegt es beim naechsten Oeffnen.
     */
    let opened = false;

    const start = (): void => {
        const mine = ++request;
        running = true;
        ports.setLoad('loading');
        void (async () => {
            try {
                const stock = await ports.fetchAll();
                if (mine !== request) return;
                running = false;
                held = true;
                ports.setStock(stock);
                ports.setLoad('idle');
            } catch (e) {
                if (mine !== request) return;
                running = false;
                ports.setLoad('failed');
                ports.reportError(messageOf(e));
            }
        })();
    };

    return {
        // Nur holen, was fehlt: ein zweites Oeffnen ohne zwischenzeitliches
        // Neuladen der Liste arbeitet mit dem bereits geholten Bestand weiter,
        // und eine bereits laufende Abfrage wird nicht verdoppelt. Nach einem
        // Fehlschlag ist das Oeffnen zugleich der neue Versuch — sonst bliebe
        // der Dialog an der Meldung von vorhin haengen.
        open: () => {
            opened = true;
            if (!held && !running) start();
        },
        retry: () => start(),
        // Der Ladezustand faellt zurueck, das laufende Laden wird verworfen.
        // Bliebe er auf 'loading' stehen, waere der Dialog beim naechsten
        // Oeffnen tot: er zeigte dauerhaft die Ladezeile, und nichts holte den
        // Bestand noch einmal.
        close: () => {
            opened = false;
            request += 1;
            running = false;
            ports.setLoad('idle');
        },
        invalidate: () => {
            request += 1;
            running = false;
            held = false;
            ports.setStock(null);
            // Offener Dialog: sofort neu holen. Sonst stuende er bis zum
            // Schliessen und Wiederoeffnen vor einer Ladezeile, hinter der
            // nichts mehr laeuft — genau die Sackgasse, gegen die es hier geht.
            // Der Weg dorthin ist kurz: jedes Neuladen der Liste (Aktualisieren,
            // Rueckkehr aus dem Editor, Kontoauszug, Rechnungslauf) verwirft den
            // Bestand, und der Dialog kann dabei offen stehen.
            if (opened) start();
            else ports.setLoad('idle');
        },
    };
};
